import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  PointsEntry,
  PointsHistory,
  PointsRewards,
} from '@pons/contracts';
import { formatAccountPoints } from './format';
import { accountFetch } from '../auth/api';

function mergeEntries(previous: PointsEntry[], incoming: PointsEntry[]) {
  const entries = new Map(previous.map((entry) => [entry.id, entry]));
  for (const entry of incoming) entries.set(entry.id, entry);
  return [...entries.values()].sort((a, b) =>
    BigInt(a.id) > BigInt(b.id) ? -1 : BigInt(a.id) < BigInt(b.id) ? 1 : 0,
  );
}

type Command = 'daily' | 'welcome' | 'more';
type State = {
  accountId?: string;
  data?: PointsRewards;
  busy: boolean;
  error: string;
  loadingMore: boolean;
  historyError: string;
  requestVersion: number;
  serverOffset: number;
};

export function useAccountPoints(
  accountId: string | undefined,
  visible: boolean,
) {
  const [state, setState] = useState<State>({
    accountId,
    busy: false,
    error: '',
    loadingMore: false,
    historyError: '',
    requestVersion: 0,
    serverOffset: 0,
  });
  const command = useRef<(action?: Command) => void>(() => {});

  useEffect(() => {
    const controller = new AbortController();
    let data: PointsRewards | undefined;
    let serverOffset = 0;
    let running = false;
    let refreshPending = false;
    let loadingMore = false;
    let historyError = '';
    let requestVersion = 0;
    const publish = (busy: boolean, error = '') => {
      if (!controller.signal.aborted)
        setState({
          accountId,
          data,
          busy,
          error,
          loadingMore,
          historyError,
          requestVersion,
          serverOffset,
        });
    };
    const fetchBody = async (path: string, reward?: 'daily' | 'welcome') => {
      const response = await accountFetch(accountId, path, {
        ...(reward
          ? {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ reward }),
            }
          : {}),
        signal: AbortSignal.any([
          controller.signal,
          AbortSignal.timeout(15000),
        ]),
        cache: 'no-store',
      });
      const body = await response.json();
      if (!response.ok)
        throw Error(body.error || 'Points are unavailable. Please try again.');
      return body;
    };
    const fetchHistory = (cursor: string): Promise<PointsHistory> =>
      fetchBody('/api/points/history?cursor=' + encodeURIComponent(cursor));

    const request = async (action?: Command) => {
      if (!accountId || controller.signal.aborted) return;
      if (running) {
        // A mutation/return event during pagination must still refresh the head.
        if (!action) refreshPending = true;
        return;
      }
      if (action === 'more' && !data?.history.nextCursor) return;
      running = true;
      loadingMore = action === 'more';
      if (loadingMore) historyError = '';
      publish(true);
      let error = '';
      try {
        if (action === 'more' && data?.history.nextCursor) {
          const cursor = data.history.nextCursor;
          const history = await fetchHistory(cursor);
          if (
            history.nextCursor &&
            BigInt(history.nextCursor) >= BigInt(cursor)
          )
            throw Error('Could not load points history. Please try again.');
          data = {
            ...data,
            history: {
              ...history,
              entries: mergeEntries(data.history.entries, history.entries),
            },
          };
        } else {
          const reward =
            action === 'daily' || action === 'welcome' ? action : undefined;
          const snapshot: PointsRewards = await fetchBody(
            reward ? '/api/points/claim' : '/api/points/rewards',
            reward,
          );
          const offset = snapshot.serverTime - Date.now();
          const previous = data?.history;
          if (previous?.entries.length) {
            const anchor = BigInt(previous.entries[0].id);
            let page = snapshot.history;
            let fresh = page.entries;
            // More than one page may have arrived while the user was away.
            // Read back to the old head before joining the immutable old tail.
            while (
              page.nextCursor &&
              (!page.entries.length || BigInt(page.entries.at(-1)!.id) > anchor)
            ) {
              const cursor = page.nextCursor;
              page = await fetchHistory(cursor);
              if (page.nextCursor && BigInt(page.nextCursor) >= BigInt(cursor))
                throw Error('Could not load points history. Please try again.');
              fresh = fresh.concat(page.entries);
            }
            snapshot.history = {
              entries: mergeEntries(previous.entries, fresh),
              nextCursor: previous.nextCursor,
            };
          }
          data = snapshot;
          serverOffset = offset;
        }
      } catch (cause) {
        const message =
          cause instanceof Error
            ? cause.message
            : 'Could not load points. Please try again.';
        if (action === 'more') historyError = message;
        else error = message;
      } finally {
        running = false;
        loadingMore = false;
        requestVersion++;
        publish(false, error);
        if (refreshPending && !controller.signal.aborted) {
          refreshPending = false;
          void request();
        }
      }
    };
    command.current = (action) => void request(action);
    publish(false);
    void request();
    const refresh = () => {
      if (!document.hidden) void request();
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('pons:points-changed', refresh);
    const timer = setInterval(refresh, 5000);
    return () => {
      controller.abort();
      command.current = () => {};
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pons:points-changed', refresh);
      clearInterval(timer);
    };
  }, [accountId]);

  const wasVisible = useRef(visible);
  useEffect(() => {
    if (visible && !wasVisible.current) command.current();
    wasVisible.current = visible;
  }, [visible]);
  const current = state.accountId === accountId ? state : undefined;
  const data = current?.data;
  const serverOffset = current?.serverOffset ?? 0;
  useEffect(() => {
    if (!data || !visible) return;
    const timer = setTimeout(
      () => command.current(),
      Math.max(100, data.nextReset - (Date.now() + serverOffset) + 100),
    );
    return () => clearTimeout(timer);
  }, [data, visible, serverOffset]);
  const loadMore = useCallback(() => command.current('more'), []);
  return {
    data,
    busy: current?.busy ?? false,
    error: current?.error ?? '',
    loadingMore: current?.loadingMore ?? false,
    historyError: current?.historyError ?? '',
    requestVersion: current?.requestVersion ?? 0,
    serverOffset,
    formattedBalance: data ? formatAccountPoints(data.balance) : '—',
    claim: (reward: 'daily' | 'welcome') => command.current(reward),
    refresh: () => command.current(),
    loadMore,
  };
}
export type AccountPoints = ReturnType<typeof useAccountPoints>;
